import React, { useState } from 'react';
import catalogoCa from '../../i18n/catalogo';
import axios from 'axios';
import { API } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { ArrowRight, ArrowLeft, Check, X, Calendar, Clock } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import { formatearFecha } from '../../i18n/fechas';
import './cambioFlowStep2.css';
import './cambioFlowStep3.css';

// ¿Clase (fecha 'YYYY-MM-DD' + hora 'HH:MM') YA HA PASADO en Europe/Madrid?
// (Gestor de cambios · Ann/Guardián 2026-07-13). Defensivo: el backend ya filtra el
// destino; esto evita que una hora pasada de hoy se pinte por caché/zona horaria.
const claseYaPasoMadrid = (fecha, hora) => {
  if (!fecha || !hora) return false;
  try {
    const [h, m] = String(hora).split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return false;
    const ahoraMadrid = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Madrid' }));
    const claseDt = new Date(`${fecha}T00:00:00`);
    claseDt.setHours(h, m, 0, 0);
    return claseDt <= ahoraMadrid;
  } catch (e) {
    return false;
  }
};

const CambioFlow = ({ monthData, selectedClase, bonificacion, onClose, onComplete }) => {
  // i18n zona cliente (interior CA · F2): namespace gestor
  const { t } = useTranslation('gestor');
  const { user } = useAuth();
  // Modo bonificación: MISMO modal y MISMA operativa de aforo REAL. El "origen"
  // es la clase regalo (no una clase fija): se elige la clase libre (aforo real)
  // y se canjea, ocupando plaza de verdad (sin overbooking).
  // Bonificación: nueva (prop bonificacion) o MOVER una ya reservada (clic en la
  // clase bonificada de la rejilla, tipo 'bonificada'). Mismo modal en ambos casos.
  const bonifMove = (selectedClase && selectedClase.tipo === 'bonificada') ? selectedClase : null;
  const bonifData = bonificacion || bonifMove;
  const esBonif = !!bonifData;
  const _bonifCreditoId = bonifData?.credito_id;
  const [step, setStep] = useState((selectedClase || esBonif) ? 2 : 1);
  const [selectedOriginal, setSelectedOriginal] = useState(
    esBonif ? { esBonificacion: true, fecha: null, hora: null, motivo: bonifData?.motivo } : (selectedClase || null)
  );
  const [selectedNew, setSelectedNew] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(0); // pestaña de semana activa (Paso 2)
  const [loading, setLoading] = useState(false);
  const [showSameDayWarning, setShowSameDayWarning] = useState(false);
  const [showDeudaWarning, setShowDeudaWarning] = useState(false);
  const [deudaMessage, setDeudaMessage] = useState('');

  // If selectedClase is provided, load available slots automatically
  // (salvo bonificada: esa usa el cargador de bonificación de abajo).
  React.useEffect(() => {
    if (selectedClase && selectedClase.tipo !== 'bonificada') {
      handleSelectOriginal(selectedClase);
    }
  }, [selectedClase]);

  // Modo bonificación: cargar las clases LIBRES del crédito (aforo REAL) en el
  // mismo Paso 2. Es el mismo modal; solo cambia el origen de la lista.
  React.useEffect(() => {
    if (!esBonif || !_bonifCreditoId) return;
    (async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/client/bonificaciones/${_bonifCreditoId}/clases-disponibles`);
        setAvailableSlots((response.data || []).filter(s => !claseYaPasoMadrid(s.fecha, s.hora)));
        setStep(2);
      } catch (error) {
        toast.error(error.response?.data?.detail || t('errores.cargarClases'));
      } finally {
        setLoading(false);
      }
    })();
  }, [esBonif, _bonifCreditoId]);

  // i18n F2: fechas por el helper (locale = idioma activo es/ca).
  const formatDate = (dateStr) => formatearFecha(dateStr + 'T00:00:00', {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  });

  const formatShortDate = (dateStr) => formatearFecha(dateStr + 'T00:00:00', {
    day: '2-digit',
    month: '2-digit'
  });

  const getDayName = (dateStr) => formatearFecha(dateStr + 'T00:00:00', { weekday: 'long' });

  const handleSelectOriginal = async (clase) => {
    setSelectedOriginal(clase);
    setLoading(true);

    try {
      // IMPORTANTE: Para peticiones de disponibilidad usaremos SIEMPRE la fecha/hora vigente.
      // Así el backend evalúa p.ej. las 3h de margen sobre el slot actual, no sobre un ancestro de un re-cambio pasado.
      const fechaVigente = clase.fecha;
      const horaVigente = clase.hora;
      
      console.log('Loading available slots for:', {
        tipo: clase.tipo,
        fechaVigente,
        horaVigente,
        isRectification: !!clase.fecha_original
      });
      
      const response = await axios.get(`${API}/client/clases-disponibles-cambio`, {
        params: {
          fecha_original: fechaVigente,
          hora_original: horaVigente
        }
      });
      setAvailableSlots(response.data);
      setStep(2);
    } catch (error) {
      // Detectar si el error es por deuda (status 403)
      if (error.response?.status === 403) {
        setDeudaMessage(error.response?.data?.detail || t('errores.deudaCambio'));
        setShowDeudaWarning(true);
      } else {
        toast.error(error.response?.data?.detail || t('errores.cargarHorarios'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Paso 2 rediseñado: al pulsar una tarjeta SOLO se selecciona (resalta); avanzar es con "Continuar".
  const handleSelectNew = (slot) => {
    setSelectedNew(slot);
  };

  const handleConfirm = async (skipWarning = false) => {
    if (!skipWarning) {
      // Verificar si la nueva clase genera múltiples clases el mismo día
      const nuevaFecha = selectedNew.fecha;
      const clasesDelMes = monthData.clases_del_mes || [];
      
      // Contar clases activas en la nueva fecha (excluyendo la clase original que se está cambiando)
      const clasesEnMismaFecha = clasesDelMes.filter(clase => 
        clase.fecha === nuevaFecha && 
        clase.estado === 'activa' &&
        !(clase.fecha === selectedOriginal.fecha && clase.hora === selectedOriginal.hora)
      );
      
      // Si ya hay al menos una clase en esa fecha, mostrar advertencia
      if (clasesEnMismaFecha.length >= 1) {
        console.log('⚠️ Múltiples clases detectadas el mismo día:', nuevaFecha);
        setShowSameDayWarning(true);
        return;
      }
    }
    
    setLoading(true);

    try {
      // Modo bonificación: MISMA operativa/aforo real. Si es MOVER una ya
      // reservada → PUT /mover (no gasta crédito). Si es nueva → POST /canjear.
      if (esBonif) {
        if (bonifMove) {
          await axios.put(`${API}/client/bonificaciones/canje/${bonifMove.canje_id}/mover`, {
            fecha: selectedNew.fecha,
            hora: selectedNew.hora,
          });
        } else {
          await axios.post(`${API}/client/bonificaciones/canjear`, {
            credito_id: _bonifCreditoId,
            fecha: selectedNew.fecha,
            hora: selectedNew.hora,
          });
        }
        toast.success(t('exito.bonificada', { dia: getDayName(selectedNew.fecha), fecha: formatShortDate(selectedNew.fecha), hora: selectedNew.hora }));
        onComplete();
        return;
      }

      // Check if this is a rectification (class has cambio_id and is in grace period)
      const isRectification = selectedOriginal.cambio_id && selectedOriginal.is_in_grace_period;
      
      if (isRectification) {
        // Use rectify endpoint - use original fecha/hora from the cambio
        const fechaOriginal = selectedOriginal.fecha_original || selectedOriginal.fecha;
        const horaOriginal = selectedOriginal.hora_original || selectedOriginal.hora;

        // Rectificación: el PUT /rectify ya actualiza el cambio EN SITIO (mismo doc,
        // sin consumir un cambio adicional). NO se hace POST aquí: un POST crearía un
        // SEGUNDO cambio activo y duplicaría el cambio (bug confirmado caso AAnn Pruebas).
        await axios.put(`${API}/client/cambio/${selectedOriginal.cambio_id}/rectify`, {
          fecha_original: fechaOriginal,
          hora_original: horaOriginal,
          fecha_nueva: selectedNew.fecha,
          hora_nueva: selectedNew.hora
        });

        toast.success(t('exito.rectificado'));
      } else {
        // Cambio normal: POST /client/cambio directo
        await axios.post(`${API}/client/cambio`, {
          fecha_original: selectedOriginal.fecha,
          hora_original: selectedOriginal.hora,
          fecha_nueva: selectedNew.fecha,
          hora_nueva: selectedNew.hora
        });

        const mensaje = t('exito.cambiado', {
          diaOrig: getDayName(selectedOriginal.fecha), fechaOrig: formatShortDate(selectedOriginal.fecha), horaOrig: selectedOriginal.hora,
          diaNueva: getDayName(selectedNew.fecha), fechaNueva: formatShortDate(selectedNew.fecha), horaNueva: selectedNew.hora,
        });
        toast.success(mensaje);
      }

      onComplete();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errores.realizarCambio'));
      setLoading(false);
    }
  };

  const handleConfirmSameDay = () => {
    setShowSameDayWarning(false);
    handleConfirm(true); // skipWarning = true
  };

  const handleCancelSameDay = () => {
    setShowSameDayWarning(false);
  };

  // Get available classes to change (active classes that aren't expired and are at least 3 hours in the future)
  const getAvailableClasses = () => {
    // Use server time from monthData, fallback to local time if not available
    const now = monthData.hora_actual_servidor 
      ? new Date(monthData.hora_actual_servidor) 
      : new Date();
    
    // Add 3 hours to current time (minimum notice required)
    const minTime = new Date(now.getTime() + (3 * 60 * 60 * 1000));
    
    return (monthData.clases_del_mes || []).filter(clase => {
      // Skip expired classes
      if (clase.estado === 'caducada') return false;
      
      // Create date object for the class
      const claseDateTime = new Date(`${clase.fecha}T${clase.hora}:00`);
      
      // Only show classes that are at least 3 hours in the future
      return claseDateTime > minTime;
    });
  };

  // Group available slots by date
  const groupSlotsByDate = () => {
    const grouped = {};
    availableSlots.forEach(slot => {
      if (!grouped[slot.fecha]) {
        grouped[slot.fecha] = [];
      }
      grouped[slot.fecha].push(slot);
    });
    return grouped;
  };

  // Agrupa los días por SEMANA (lunes-domingo) con etiquetas claras para gente mayor:
  // "Esta semana", "Semana que viene", "En 2 semanas"... + rango de fechas. Solo cabeceras,
  // sin pasos nuevos: la lista sigue siendo de un vistazo y un toque.
  const groupSlotsByWeek = () => {
    const porFecha = groupSlotsByDate();
    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    const dowLun = (hoy.getDay() + 6) % 7; // 0 = lunes
    const lunesActual = new Date(hoy); lunesActual.setDate(hoy.getDate() - dowLun);
    const semanas = {};
    Object.keys(porFecha).sort().forEach(fecha => {
      const f = new Date(fecha + 'T00:00:00');
      const idx = Math.max(0, Math.floor((f - lunesActual) / 86400000 / 7));
      if (!semanas[idx]) semanas[idx] = { idx, dias: [] };
      semanas[idx].dias.push([fecha, porFecha[fecha]]);
    });
    const etiqueta = (idx) => idx === 0 ? t('semanas.esta') : idx === 1 ? t('semanas.queViene') : t('semanas.enN', { n: idx });
    const rango = (idx) => {
      const ini = new Date(lunesActual); ini.setDate(lunesActual.getDate() + idx * 7);
      const fin = new Date(ini); fin.setDate(ini.getDate() + 6);
      const fmt = (d) => formatearFecha(d, { day: '2-digit', month: 'short' });
      return `${fmt(ini)} – ${fmt(fin)}`;
    };
    return Object.values(semanas).sort((a, b) => a.idx - b.idx)
      .map(s => ({ label: etiqueta(s.idx), rango: rango(s.idx), dias: s.dias }));
  };

  // Límite real de cambios (config). Fallback 7 solo si el backend aún no manda max_cambios_mes.
  const _maxCambios = monthData?.max_cambios_mes ?? 10; // fallback provisional (config real llega del backend al desplegar)
  const _cambiosUsados = monthData?.cambios_mes ?? 0;
  const _pctCambios = Math.min(100, Math.max(0, (_cambiosUsados / (_maxCambios || 1)) * 100));

  return (
    <Dialog open={true} onOpenChange={onClose}>
      {/* PANTALLA COMPLETA (Ann 2026-07-28: fuera el marco con scroll — se baja por la pagina y punto) */}
      <DialogContent className="overflow-y-auto z-[60] rounded-none" data-testid="cambio-flow"
        style={{ zIndex: 60, left: 0, top: 0, transform: 'none', width: '100vw', maxWidth: '100vw', height: '100dvh', maxHeight: '100dvh', borderRadius: 0 }}>
        <DialogHeader className="sr-only">
          <DialogTitle>{t('flujo.tituloSr')}</DialogTitle>
          <DialogDescription>{t('flujo.descSr')}</DialogDescription>
        </DialogHeader>

        {/* Step 1: Select Original Class */}
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{t('paso1.titulo')}</h2>
              <p className="text-gray-600 mt-1">{t('paso1.sub')}</p>
            </div>

            <div className="space-y-3">
              {getAvailableClasses().length > 0 ? (
                getAvailableClasses().map((clase, index) => (
                  <Card
                    key={index}
                    data-testid={`original-class-${index}`}
                    className="border-2 border-teal-200 hover:border-teal-400 cursor-pointer hover:shadow-lg transition-all"
                    onClick={() => handleSelectOriginal(clase)}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <Calendar className="w-6 h-6 text-teal-500" />
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900 capitalize">
                              {formatDate(clase.fecha)}
                            </h3>
                            <p className="text-2xl font-bold text-teal-600 flex items-center gap-2">
                              <Clock className="w-5 h-5" />
                              {clase.hora}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {clase.tipo === 'fija' ? t('paso1.claseFija') : t('paso1.claseCambiada')}
                            </p>
                          </div>
                        </div>
                        <ArrowRight className="w-6 h-6 text-teal-500" />
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  {t('paso1.sinClases')}
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <Button
                data-testid="cancel-flow-button"
                onClick={onClose}
                variant="outline"
              >
                <X className="w-4 h-4 mr-2" />
                {t('comun.cancelar')}
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Select New Class · rediseño (spec Ann) */}
        {step === 2 && (
          <div className="cf2">
            <div>
              <span className="step-pill"><span className="n">2</span><span className="lbl">{t('paso2.pill')}</span></span>
              <h2 className="cf2-title">{t('paso2.titulo')}</h2>
              <div className="summary">
                <span className="chip from capitalize">
                  {esBonif
                    ? t('comun.claseBonificada')
                    : `${getDayName(selectedOriginal.fecha)} ${formatShortDate(selectedOriginal.fecha)} · ${selectedOriginal.hora}`}
                </span>
                <span className="arrow"><ArrowRight className="w-4 h-4" /></span>
                <span className="chip to">
                  {t('paso2.centro', { sede: availableSlots.length > 0 && availableSlots[0].sede ? availableSlots[0].sede : (user?.sede || 'MUNTANER') })}
                </span>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
              </div>
            ) : availableSlots.length > 0 ? (
              (() => {
                const semanas = groupSlotsByWeek();
                const idx = Math.min(selectedWeek, semanas.length - 1);
                const activa = semanas[idx] || semanas[0];
                return (
                  <>
                    {/* Pestañas de semana */}
                    <div className="weeks">
                      {semanas.map((w, wi) => (
                        <button key={wi} type="button" className={`week ${wi === idx ? 'active' : ''}`} onClick={() => setSelectedWeek(wi)}>
                          {w.label}<span>{w.rango}</span>
                        </button>
                      ))}
                    </div>
                    {/* Días de la semana activa */}
                    {/* SIN scroll interno (Ann 2026-07-26: «odio los scrolls innecesarios»):
                        la lista de días fluye entera y se baja con el scroll normal;
                        para cambiar de semana están las pestañas/botón de semana. */}
                    <div className="pr-1">
                      {activa && activa.dias.map(([fecha, slots]) => (
                        <div key={fecha}>
                          <div className="day">
                            <span className="bar" />
                            <h3>{formatDate(fecha)}</h3>
                            <span className="count">{t('paso2.numClases', { count: slots.length })}</span>
                          </div>
                          <div className="grid">
                            {slots.map((slot, index) => {
                              const currentFecha = selectedOriginal.fecha;
                              const currentHora = selectedOriginal.hora;
                              const isExactCurrentSlot = slot.fecha === currentFecha && slot.hora === currentHora;
                              const clasesDelMes = monthData?.clases_del_mes || [];
                              const esMismoDiaQueOriginal = slot.fecha === currentFecha;
                              const tieneOtraClaseEsteDia = !esMismoDiaQueOriginal && clasesDelMes.some(clase =>
                                clase.fecha === slot.fecha && clase.estado === 'activa' &&
                                !(clase.fecha === currentFecha && clase.hora === currentHora)
                              );
                              const esCompleta = slot.completa === true;
                              const isBloqueadoPorAdmin = slot.bloqueado === true && !esCompleta;
                              const motivoBloqueo = slot.motivo_bloqueo;
                              const isBlocked = isExactCurrentSlot || tieneOtraClaseEsteDia || isBloqueadoPorAdmin || esCompleta;
                              const isSel = selectedNew && selectedNew.fecha === slot.fecha && selectedNew.hora === slot.hora;
                              let blockMessage = '';
                              if (isBloqueadoPorAdmin) blockMessage = motivoBloqueo || t('paso2.noDisponible');
                              else if (isExactCurrentSlot) blockMessage = t('paso2.tuClaseActualMsg');
                              else if (tieneOtraClaseEsteDia) blockMessage = t('paso2.yaReservada');
                              const plazas = slot.plazas_libres;
                              return (
                                <button
                                  key={index}
                                  type="button"
                                  data-testid={`new-class-${fecha}-${slot.hora}`}
                                  className={`class ${isBlocked ? 'disabled' : ''} ${isSel ? 'selected' : ''} ${(!isBlocked && (slot.intensidad_nombre || '').toLowerCase().includes('suave')) ? 'suave' : ''}`}
                                  onClick={() => !isBlocked && handleSelectNew(slot)}
                                  disabled={isBlocked}
                                  title={esCompleta ? t('paso2.claseCompleta') : blockMessage}
                                >
                                  <div className="row">
                                    <span className="time">{slot.hora}</span>
                                    {esCompleta ? <span className="badge full">{t('paso2.completa')}</span>
                                      : isBloqueadoPorAdmin ? <span className="badge full">{t('paso2.noDisponibleBadge')}</span>
                                      : isExactCurrentSlot ? <span className="badge full">{t('paso2.tuClaseActual')}</span>
                                      : tieneOtraClaseEsteDia ? <span className="badge full">{t('paso2.ocupado')}</span>
                                      : plazas === 1 ? <span className="badge warn">{t('paso2.unaPlaza')}</span>
                                      : <span className="badge ok">{t('paso2.plazasLibres', { n: plazas })}</span>}
                                  </div>
                                  <div className="meta">
                                    <strong>{catalogoCa(slot.intensidad_nombre) || t('comun.clase')}</strong>{slot.sede ? ` · ${slot.sede}` : ''}
                                    {tieneOtraClaseEsteDia && <div style={{ fontWeight: 700, color: '#66757B' }}>{t('paso2.yaReservada')}</div>}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500 text-lg">{t('paso2.sinClasesSede')}</p>
                <p className="text-gray-400 text-sm mt-2">{t('paso2.intentaMasTarde')}</p>
              </div>
            )}

            {/* Pie fijo · Atrás / Continuar (Continuar deshabilitado hasta elegir clase) */}
            <div className="cf2-foot">
              <button
                type="button"
                className="btn-back"
                data-testid="back-to-step-1-button"
                onClick={() => {
                  if (esBonif) { onClose(); return; } // en bonificación no hay Paso 1
                  setStep(1); setSelectedOriginal(null); setSelectedNew(null); setAvailableSlots([]); setSelectedWeek(0);
                }}
              >
                {esBonif ? t('comun.cancelar') : t('paso2.atras')}
              </button>
              <button
                type="button"
                className="btn-next"
                data-testid="continue-step-2-button"
                disabled={!selectedNew}
                onClick={() => setStep(3)}
              >
                {t('comun.continuar')} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Confirm · rediseño (maqueta Ann) */}
        {step === 3 && (
          <div className="cf3">
            <span className="c3-step-pill"><span className="n">3</span><span className="lbl">{t('paso3.pill')}</span></span>
            <h2 className="c3-title">{esBonif ? t('paso3.confirmarBonif') : t('paso3.confirmarCambio')}</h2>
            <p className="c3-sub">{t('paso3.sub')}</p>

            {/* de → a */}
            <div className="c3-swap">
              <div className="c3-slot from">
                <div className="c3-lbl">{esBonif ? t('comun.claseBonificada') : t('paso3.claseActual')}</div>
                {esBonif ? (
                  <div className="c3-date">{t('paso3.deRegalo')}</div>
                ) : (
                  <>
                    <div className="c3-date">{getDayName(selectedOriginal.fecha)} {formatShortDate(selectedOriginal.fecha)}</div>
                    <div className="c3-time">{selectedOriginal.hora}</div>
                    {selectedOriginal.sede && <div className="c3-meta">{selectedOriginal.sede}</div>}
                  </>
                )}
              </div>
              <div className="c3-arrow"><span><ArrowRight className="w-5 h-5" /></span></div>
              <div className="c3-slot to">
                <div className="c3-lbl">{t('paso3.nuevaClase')}</div>
                <div className="c3-date">{getDayName(selectedNew.fecha)} {formatShortDate(selectedNew.fecha)}</div>
                <div className="c3-time">{selectedNew.hora}</div>
                {selectedNew.intensidad_nombre && <span className="c3-tag">{catalogoCa(selectedNew.intensidad_nombre)}</span>}
              </div>
            </div>

            {/* nota */}
            <div className="c3-note">
              <span className="c3-note-ic"><Check className="w-4 h-4" /></span>
              <p>
                {esBonif ? (
                  <Trans t={t} i18nKey="paso3.notaBonif" components={{ 1: <strong /> }} />
                ) : (
                  <Trans t={t} i18nKey="paso3.notaCambio" values={{ max: _maxCambios }} components={{ 1: <strong /> }} />
                )}
              </p>
            </div>

            {/* contador (real, desde la config) — solo en modo cambio */}
            {!esBonif && (
              <div className="c3-count">
                <div className="c3-bar"><i style={{ width: `${_pctCambios}%` }} /></div>
                <span className="c3-count-txt">{t('paso3.contador', { usados: _cambiosUsados, max: _maxCambios })}</span>
              </div>
            )}

            {/* pie */}
            <div className="c3-foot">
              <button className="c3-back" data-testid="back-to-step2-button" onClick={() => { setStep(2); setSelectedNew(null); }}>
                <ArrowLeft className="w-4 h-4" /> {t('comun.atras')}
              </button>
              <button className="c3-confirm" data-testid="confirm-cambio-button" onClick={handleConfirm} disabled={loading}>
                {loading ? t('comun.procesando') : (<><Check className="w-4 h-4" /> {esBonif ? t('paso3.confirmarBonif') : t('paso3.confirmarCambio')}</>)}
              </button>
            </div>
          </div>
        )}
      </DialogContent>

      {/* Modal de Advertencia - Mismo Día (CLIENTE) */}
      {showSameDayWarning && (
        <Dialog open={showSameDayWarning} onOpenChange={handleCancelSameDay}>
          <DialogContent className="max-w-md z-[70]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-orange-600 text-base sm:text-lg">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {t('avisoMismoDia.titulo')}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="p-3 sm:p-4 bg-orange-50 border border-orange-200 rounded-md">
                <p className="text-sm sm:text-base text-gray-800 leading-relaxed">
                  <Trans t={t} i18nKey="avisoMismoDia.texto" components={{ 1: <strong /> }} />
                </p>
              </div>

              <div className="flex flex-col gap-2 pt-2 pb-6 sm:pb-0">
                <Button
                  onClick={handleConfirmSameDay}
                  className="bg-orange-500 hover:bg-orange-600 w-full text-sm sm:text-base py-3 sm:py-2"
                  disabled={loading}
                >
                  {loading ? t('comun.procesando') : t('avisoMismoDia.confirmar')}
                </Button>
                <Button
                  onClick={handleCancelSameDay}
                  variant="outline"
                  className="w-full text-sm sm:text-base py-3 sm:py-2"
                  disabled={loading}
                >
                  {t('avisoMismoDia.otroDia')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Modal de Advertencia - Deuda Pendiente */}
      {showDeudaWarning && (
        <Dialog open={showDeudaWarning} onOpenChange={() => { setShowDeudaWarning(false); onClose(); }}>
          <DialogContent className="max-w-md z-[70]" data-testid="deuda-warning-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600 text-base sm:text-lg">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                {t('deudaModal.titulo')}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="p-3 sm:p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm sm:text-base text-gray-800 leading-relaxed">
                  {deudaMessage}
                </p>
              </div>

              <div className="flex flex-col gap-2 pt-2 pb-6 sm:pb-0">
                <Button
                  onClick={() => { setShowDeudaWarning(false); onClose(); }}
                  className="bg-red-500 hover:bg-red-600 w-full text-sm sm:text-base py-3 sm:py-2"
                  data-testid="deuda-warning-close-btn"
                >
                  {t('comun.entendido')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
};

export default CambioFlow;
